import React, { useState, useEffect } from 'react';
import apiClient from '../api/client';
import DataTable from '../components/DataTable';
import { format } from 'date-fns';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

const ISOAudit = () => {
  const [clauses, setClauses] = useState([]);
  const [complianceStats, setComplianceStats] = useState(null);
  const [auditReports, setAuditReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clausesRes, statsRes, reportsRes] = await Promise.all([
        apiClient.get('/iso-clauses'),
        apiClient.get('/iso-clauses/stats/compliance'),
        apiClient.get('/audit-reports')
      ]);
      setClauses(clausesRes.data);
      setComplianceStats(statsRes.data);
      setAuditReports(reportsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ISO audit data:', error);
      setLoading(false);
    }
  };

  const getComplianceColor = (status) => {
    const colors = {
      compliant: 'bg-green-100 text-green-800',
      partially_compliant: 'bg-yellow-100 text-yellow-800',
      non_compliant: 'bg-red-100 text-red-800',
      not_assessed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const clauseColumns = [
    { field: 'clause_number', header: 'Clause', sortable: true },
    { field: 'title', header: 'Title', sortable: true },
    { 
      field: 'compliance_status', 
      header: 'Compliance Status', 
      sortable: true,
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getComplianceColor(value)}`}>
          {value.replace(/_/g, ' ').toUpperCase()}
        </span>
      )
    },
    { 
      field: 'last_audit_date', 
      header: 'Last Audit', 
      sortable: true,
      render: (value) => value ? format(new Date(value), 'MMM dd, yyyy') : 'N/A'
    },
    { 
      field: 'next_audit_date', 
      header: 'Next Audit', 
      sortable: true,
      render: (value) => value ? format(new Date(value), 'MMM dd, yyyy') : 'N/A'
    }
  ];

  const reportColumns = [
    { field: 'report_name', header: 'Report Name', sortable: true },
    { 
      field: 'audit_type', 
      header: 'Type', 
      sortable: true,
      render: (value) => value.charAt(0).toUpperCase() + value.slice(1)
    },
    { 
      field: 'audit_date', 
      header: 'Date', 
      sortable: true,
      render: (value) => format(new Date(value), 'MMM dd, yyyy')
    },
    { field: 'auditor_name', header: 'Auditor', sortable: true },
    { 
      field: 'compliance_score', 
      header: 'Score', 
      sortable: true,
      render: (value) => `${value}%`
    },
    { field: 'non_conformances', header: 'Non-Conformances', sortable: true },
    { field: 'observations', header: 'Observations', sortable: true }
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  // Prepare pie chart data
  const pieData = complianceStats ? [
    { name: 'Compliant', value: complianceStats.stats.compliant, color: '#10b981' },
    { name: 'Partially Compliant', value: complianceStats.stats.partially_compliant, color: '#f59e0b' },
    { name: 'Non-Compliant', value: complianceStats.stats.non_compliant, color: '#ef4444' },
    { name: 'Not Assessed', value: complianceStats.stats.not_assessed, color: '#6b7280' }
  ].filter(item => item.value > 0) : [];

  return (
    <div className="space-y-6" data-testid="iso-audit-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">ISO Audit Readiness</h1>
        <p className="mt-2 text-gray-600">ISO 9001:2015 Compliance Tracking</p>
      </div>

      {/* Compliance Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Overall Compliance</h2>
          <div className="text-center">
            <div className="text-6xl font-bold text-green-600">
              {complianceStats?.compliance_percentage || 0}%
            </div>
            <p className="mt-2 text-gray-600">Compliance Score</p>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{complianceStats?.stats.compliant || 0}</p>
              <p className="text-sm text-gray-600">Compliant</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{complianceStats?.stats.partially_compliant || 0}</p>
              <p className="text-sm text-gray-600">Partial</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Compliance Distribution</h2>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ISO Clauses Table */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">ISO 9001:2015 Clauses</h2>
        <DataTable columns={clauseColumns} data={clauses} />
      </div>

      {/* Audit Reports */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Audit Reports</h2>
        <DataTable columns={reportColumns} data={auditReports} />
      </div>
    </div>
  );
};

export default ISOAudit;